'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { Project, getImageUrl, getVideoUrl } from '../lib/sanity'
import Image from 'next/image'

interface CinemaProjectViewProps {
  project: Project
  onClose: () => void
  onNext: () => void
  onPrev: () => void
  currentMediaIndex: number
  totalMedia: number
  nextProjectId?: string
  nextProject?: Project
  /** When set (e.g. home featured carousel), top-left shows project position among featured: 1/5 */
  featuredProjectPosition?: number
  featuredProjectsTotal?: number
}

const CinemaProjectView = ({
  project,
  onClose,
  onNext,
  onPrev,
  currentMediaIndex,
  totalMedia,
  nextProjectId,
  nextProject,
  featuredProjectPosition,
  featuredProjectsTotal
}: CinemaProjectViewProps) => {
  const [isPlaying, setIsPlaying] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [showProjectInfo, setShowProjectInfo] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [hoveredNextProject, setHoveredNextProject] = useState(false)
  const [hoveredNavZone, setHoveredNavZone] = useState<'left' | 'right' | null>(null)

  const videoRef = useRef<HTMLVideoElement>(null)
  const onCloseRef = useRef(onClose)
  const onNextRef = useRef(onNext)
  const onPrevRef = useRef(onPrev)

  useEffect(() => {
    onCloseRef.current = onClose
    onNextRef.current = onNext
    onPrevRef.current = onPrev
  }, [onClose, onNext, onPrev])

  const currentMedia = project.media?.[currentMediaIndex]
  const isVideo =
    !!(currentMedia?.video?.asset?._ref || currentMedia?.video?.asset?.url) ||
    (currentMedia?.type === 'video' && !!currentMedia.video)
  const isImage =
    !!(currentMedia?.image?.asset?._ref || currentMedia?.image?.asset?.url) ||
    (currentMedia?.type === 'image' && !!currentMedia.image)
  const isLastMedia = currentMediaIndex === totalMedia - 1

  // Mouse: subscribe once so parent re-renders (navigation) do not tear down listeners and reset cursor to 0,0
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY })
    }
    document.addEventListener('mousemove', handleMouseMove)
    return () => document.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const handlePlayPause = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (!videoRef.current) return
    if (isPlaying) {
      videoRef.current.pause()
    } else {
      videoRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }, [isPlaying])

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current()
      } else if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault()
        handlePlayPause()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        onNextRef.current()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        onPrevRef.current()
      }
    }

    document.addEventListener('keydown', handleKeyPress)

    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [handlePlayPause])

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    if (!videoRef.current) return
    const rect = e.currentTarget.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    videoRef.current.currentTime = percent * duration
  }

  const handleMuteToggle = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (!videoRef.current) return
    videoRef.current.muted = !videoRef.current.muted
    setIsMuted(videoRef.current.muted)
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  // Smart positioning logic for floating info
  const getSmartPosition = () => {
    // Only run on client side to avoid window access during SSR
    if (typeof window === 'undefined') {
      return {
        left: mousePosition.x + 20,
        top: mousePosition.y - 40
      }
    }

    const edgeThreshold = 200 // Distance from edge to trigger repositioning
    const infoWidth = 150 // Approximate width of info box
    const infoHeight = 50 // Approximate height of info box
    
    // Check if cursor is near edges
    const nearRightEdge = mousePosition.x > window.innerWidth - edgeThreshold
    const nearLeftEdge = mousePosition.x < edgeThreshold
    const nearBottomEdge = mousePosition.y > window.innerHeight - edgeThreshold
    const nearTopEdge = mousePosition.y < edgeThreshold

    // Calculate smart positioning
    let leftPos = mousePosition.x + 20
    let topPos = mousePosition.y - 40

    // Horizontal positioning
    if (nearRightEdge) {
      leftPos = mousePosition.x - infoWidth - 20 // Move to left of cursor
    } else if (nearLeftEdge) {
      leftPos = mousePosition.x + 20 // Keep on right of cursor
    }

    // Vertical positioning
    if (nearBottomEdge) {
      topPos = mousePosition.y - infoHeight - 20 // Move above cursor
    } else if (nearTopEdge) {
      topPos = mousePosition.y + 20 // Move below cursor
    }

    return { left: leftPos, top: topPos }
  }

  const smartPosition = getSmartPosition()

  const showFeaturedCount =
    typeof featuredProjectsTotal === 'number' &&
    featuredProjectsTotal > 0 &&
    typeof featuredProjectPosition === 'number' &&
    featuredProjectPosition >= 1

  const counterLabel = showFeaturedCount
    ? `${featuredProjectPosition}/${featuredProjectsTotal}`
    : `${currentMediaIndex + 1}/${Math.max(1, totalMedia)}`

  return (
    <div
      className="fixed inset-0 z-50 bg-black"
      style={{ cursor: 'none' }}
      onMouseMove={() => setShowProjectInfo(true)}
      onMouseLeave={() => setShowProjectInfo(false)}
    >
            {/* Custom cursor dot with navigation arrows */}
      <div 
        className="fixed z-80 pointer-events-none flex items-center"
        style={{
          left: mousePosition.x - 2,
          top: mousePosition.y - 2,
          transform: 'translate(0, 0)'
        }}
      >
        {hoveredNavZone === 'left' && (
          <span className="text-white text-xs mr-2 font-bold">&lt;</span>
        )}
        <div className="w-1 h-1 bg-white rounded-full"></div>
        {hoveredNavZone === 'right' && (
          <span className="text-white text-xs ml-2 font-bold">&gt;</span>
        )}
      </div>

      {/* Floating Project Info - follows cursor with smart edge detection */}
      {showProjectInfo && (
        <div
          className="fixed z-70 pointer-events-none text-white text-xs uppercase tracking-widest font-medium"
          style={{
            left: smartPosition.left,
            top: smartPosition.top,
            transform: 'translate(0, 0)'
          }}
        >
          {hoveredNextProject && nextProject ? (
            <>
              <div>NEXT: {nextProject.client?.name || 'Client'}</div>
              <div className="opacity-70">{nextProject.title}, {nextProject.year}</div>
            </>
          ) : (
            <>
              <div>{project.client?.name || 'Client'}</div>
              <div className="opacity-70">{project.title}, {project.year}</div>
            </>
          )}
        </div>
      )}

      {/* Close button — above z-60 nav zones (same z-60 + later DOM was blocking clicks) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        className="absolute top-6 right-6 z-[100] pointer-events-auto text-white hover:text-gray-300 transition-colors"
        style={{ cursor: 'pointer' }}
        aria-label="Close gallery"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Top left: featured index (home) or media index (work / slug) */}
      <div className="absolute top-6 left-6 z-[100] pointer-events-none text-white text-xs uppercase tracking-widest font-medium">
        {project.id} ({counterLabel})
      </div>

      
      
      {/* Next Project Button - appears on last media item, positioned next to right arrow */}
      {isLastMedia && nextProjectId && (
        <div className="absolute top-1/2 transform -translate-y-1/2 z-60" style={{ right: '6rem' }}>
          <Link
            href={`/work/${nextProjectId}`}
            className="text-white text-xs uppercase tracking-widest hover:opacity-70 transition-opacity"
            style={{ cursor: 'none' }}
            onMouseEnter={() => setHoveredNextProject(true)}
            onMouseLeave={() => setHoveredNextProject(false)}
          >
            NEXT PROJECT
          </Link>
        </div>
      )}

      {/* Background click area */}
      <div className="absolute inset-0 z-40" onClick={onClose}></div>

      {/* Main content area */}
      <div className="relative z-50 flex flex-col h-full pointer-events-none">
        {/* Media area */}
        <div className="flex-1 flex items-center justify-center p-8 pointer-events-auto">
          <div className="relative w-full h-full max-w-5xl max-h-[75vh]">
            {isVideo ? (
              <video
                ref={videoRef}
                key={`${project._id}-${currentMediaIndex}`}
                className="w-full h-full object-contain"
                autoPlay
                muted={isMuted}
                loop={false}
                playsInline
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
                onLoadedData={() => setDuration(videoRef.current?.duration || 0)}
                onEnded={() => onNextRef.current()}
              >
                {currentMedia?.video && <source src={getVideoUrl(currentMedia.video)} type="video/mp4" />}
              </video>
            ) : isImage ? (
              <Image
                src={getImageUrl(currentMedia.image!)}
                alt={currentMedia.alt || project.title}
                fill
                className="object-contain"
                sizes="100vw"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-900">
                <span className="text-gray-400">No media available</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Minimal player controls - positioned at bottom */}
      {isVideo && (
        <div className="absolute bottom-8 left-8 right-8 pointer-events-auto z-70">
          <div className="w-full min-w-[600px] max-w-6xl mx-auto space-y-3">
            {/* Progress bar */}
            <div
              className="w-full h-0.5 bg-white bg-opacity-30 overflow-hidden min-w-[500px]"
              style={{ cursor: 'pointer' }}
              onClick={handleSeek}
            >
              <div
                className="h-full bg-white transition-all duration-100"
                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
              />
            </div>

            {/* Play controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handlePlayPause}
                  className="text-white hover:text-gray-300 transition-colors"
                  style={{ cursor: 'pointer' }}
                >
                  {isPlaying ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>
                <span className="text-white text-xs uppercase tracking-wider">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
              <button
                type="button"
                onClick={handleMuteToggle}
                aria-label={isMuted ? 'Turn sound on' : 'Turn sound off'}
                className="text-white text-xs uppercase tracking-wider hover:text-gray-300 transition-colors"
                style={{ cursor: 'pointer' }}
              >
                {isMuted ? 'SOUND ON' : 'SOUND OFF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Zones - invisible but hoverable */}
      <div 
        className="absolute top-0 left-0 w-1/2 h-full z-60 nav-zone"
        onMouseEnter={() => setHoveredNavZone('left')}
        onMouseLeave={() => setHoveredNavZone(null)}
        onClick={(e) => {
          e.stopPropagation()
          setMousePosition({ x: e.clientX, y: e.clientY })
          onPrev()
        }}
        style={{ cursor: 'none' }}
      />
      <div
        className="absolute top-0 right-0 w-1/2 h-full z-60 nav-zone"
        onMouseEnter={() => setHoveredNavZone('right')}
        onMouseLeave={() => setHoveredNavZone(null)}
        onClick={(e) => {
          e.stopPropagation()
          setMousePosition({ x: e.clientX, y: e.clientY })
          onNext()
        }}
        style={{
          cursor: 'none',
          clipPath: 'polygon(0% 0%, 80% 0%, 80% 20%, 100% 20%, 100% 100%, 0% 100%)',
        }}
      />
    </div>
  )
}

export default CinemaProjectView 